import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Send, Bot, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

export default function AIAssistantPage() {
  const { user, isAdmin } = useAuth();
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState("");
  const [messages, setMessages] = useState([]);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    if (activeThreadId) {
      fetchMessages(activeThreadId);
    } else {
      setMessages([]);
    }
  }, [activeThreadId]);

  const fetchThreads = async () => {
    setLoadingThreads(true);
    try {
      // client sees its own; admin sees all
      const res = await axios.get(`${API}/ai/threads`);
      setThreads(res.data);
      if (res.data?.length) setActiveThreadId(res.data[0].id);
    } catch (e) {
      toast.error("Erreur lors du chargement des threads IA");
    } finally {
      setLoadingThreads(false);
    }
  };

  const fetchMessages = async (threadId) => {
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/ai/threads/${threadId}/messages`);
      setMessages(res.data);
    } catch (e) {
      toast.error("Erreur lors du chargement des messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  const send = async (e) => {
    e.preventDefault();
    if (!activeThreadId) {
      toast.error("Aucun thread sélectionné");
      return;
    }
    if (!text.trim()) return;

    const optimistic = {
      id: `tmp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };

    setMessages((m) => [...m, optimistic]);
    const toSend = text;
    setText("");

    setSending(true);
    try {
      const res = await axios.post(`${API}/ai/threads/${activeThreadId}/messages`, {
        content: toSend,
      });
      setMessages((m) => [...m.filter((x) => x.id !== optimistic.id), optimistic, res.data.assistant]);
    } catch (err) {
      console.error(err);
      toast.error("Erreur IA");
      setMessages((m) => m.filter((x) => x.id !== optimistic.id));
      setText(toSend);
    } finally {
      setSending(false);
    }
  };

  const title = useMemo(() => {
    const t = threads.find((x) => x.id === activeThreadId);
    if (!t) return "Assistant IA";
    return t.title;
  }, [threads, activeThreadId]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bot className="h-6 w-6 text-[#0099CC]" />
            Assistant IA
          </h1>
          <p className="text-[#94A3B8] mt-1">
            {isAdmin
              ? "Assistant de production (multi-clients)"
              : "Assistant de production pour votre projet"}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchThreads}
          className="border-[#2A3441] text-[#94A3B8] hover:text-white"
        >
          Rafraîchir
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-3 bg-[#151B2B] border border-[#2A3441] rounded-xl p-4">
          <p className="text-sm text-[#94A3B8] mb-3 flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Threads
          </p>

          {loadingThreads ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" />
            </div>
          ) : threads.length === 0 ? (
            <p className="text-sm text-[#94A3B8]">Aucun thread IA</p>
          ) : (
            <Select value={activeThreadId} onValueChange={setActiveThreadId}>
              <SelectTrigger className="bg-[#0B0F19] border-[#2A3441] text-white">
                <SelectValue placeholder="Choisir" />
              </SelectTrigger>
              <SelectContent className="bg-[#151B2B] border-[#2A3441]">
                {threads.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="lg:col-span-9 bg-[#151B2B] border border-[#2A3441] rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#2A3441] bg-[#0B0F19]">
            <p className="text-white font-semibold">{title}</p>
          </div>

          <div className="h-[460px] overflow-y-auto p-6 space-y-4">
            {loadingMessages ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#0099CC]" />
              </div>
            ) : messages.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">Posez une question (SEO, contenu, cahier des charges…)</p>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`max-w-[900px] rounded-xl p-4 border ${
                    m.role === "assistant"
                      ? "bg-[#0B0F19] border-[#2A3441]"
                      : "bg-[#0099CC]/10 border-[#0099CC]/20"
                  }`}
                >
                  <p className="text-xs text-[#94A3B8] mb-2">
                    {m.role === "assistant" ? "Assistant" : "Vous"}
                  </p>
                  <pre className="whitespace-pre-wrap text-sm text-white font-sans">
                    {m.content}
                  </pre>
                </div>
              ))
            )}
          </div>

          <form onSubmit={send} className="p-4 border-t border-[#2A3441] bg-[#0B0F19] flex gap-3">
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ex: Rédige une page service 'Vitrerie Villiers-sur-Marne'…"
              className="bg-[#151B2B] border-[#2A3441] text-white"
            />
            <Button type="submit" disabled={sending} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
